import { useCallback, useRef, useState } from "react";
import { createPortal } from "react-dom";

// A lightweight hover/focus tooltip that renders into document.body with position: fixed, so it
// is never clipped by a scroll container or an overflow-hidden table cell (where a plain absolute
// tooltip would be cut off). Shows instantly, honours line breaks in `content`, and is
// pointer-transparent so it never eats the hover. Renders nothing when `content` is empty.
//
// `variant` picks the look: "dark" is the compact label for short hints; "card" is a light
// popover for the richer, multi-line explanations, matching the app's dropdown/menu surfaces.
const VARIANTS = {
  dark: {
    className:
      "rounded-md bg-zinc-900 px-2.5 py-1.5 text-xs font-medium leading-snug text-white shadow-lg",
    maxWidth: "280px",
  },
  card: {
    className:
      "rounded-lg bg-white px-4 py-3 text-[13px] leading-relaxed text-zinc-600 shadow-xl ring-1 ring-black/5 [&_strong]:font-semibold [&_strong]:text-zinc-900",
    maxWidth: "340px",
  },
};

export default function Tooltip({
  content,
  children,
  className,
  wrapperClassName,
  placement = "top",
  variant = "dark",
}) {
  const [coords, setCoords] = useState(null);
  const ref = useRef(null);
  const style = VARIANTS[variant] ?? VARIANTS.dark;

  const show = useCallback(() => {
    const el = ref.current;
    if (!el || !content) return;
    const rect = el.getBoundingClientRect();
    if (placement === "right") {
      setCoords({ x: rect.right, y: rect.top + rect.height / 2 });
      return;
    }
    setCoords({
      x: rect.left + rect.width / 2,
      y: placement === "bottom" ? rect.bottom : rect.top,
    });
  }, [content, placement]);

  const hide = useCallback(() => setCoords(null), []);

  return (
    <span
      ref={ref}
      className={wrapperClassName}
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
    >
      {children}
      {coords &&
        createPortal(
          <span
            role="tooltip"
            style={{
              position: "fixed",
              left: coords.x,
              top: coords.y,
              transform:
                placement === "right"
                  ? "translate(8px, -50%)"
                  : placement === "bottom"
                  ? "translate(-50%, 8px)"
                  : "translate(-50%, calc(-100% - 8px))",
              zIndex: 100,
              maxWidth: style.maxWidth,
            }}
            className={
              className ?? `pointer-events-none block whitespace-pre-line ${style.className}`
            }
          >
            {content}
          </span>,
          document.body,
        )}
    </span>
  );
}
